import { QueryResult } from "@/api/utils/types"
import {
  MeterpointParser,
  IMeterpointRaw,
} from "@/models/meterpoint/meterpoint.parser"
import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
import { MeterpointSupplyHistoryParser } from "@/models/meterpointSupplyHistory/meterpointSupplyHistory.parser"
import { BackofficeTariffAgreementsParser } from "@/models/tariffAgreements/tariffAgreementsBackoffice.parser"
import { TariffAgreements } from "@/models/tariffAgreements/tariffAgreements"
import { ITariffAgreements } from "@/models/tariffAgreements/tariffAgreements.types"
import { ISupplyHistory } from "@/models/meterpointSupplyHistory/meterpointSupplyHistory.types"
import { useQuery } from "@/api/utils/useQuery"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import {
  AggregatedQueryProps,
  QueryAggregator,
} from "@/api/utils/QueryAggregator"
import { IReading } from "@/models/meterReading/meterReading.types"
import {
  AlpsRangeParser,
  buildAlpsRangeVariables,
} from "@/models/readUsageCalculation/readUsageCalculation.parser"
import { mapQueryResult } from "@/api/utils/mapQuery"
import { ReadUsageCalculation } from "@/models/readUsageCalculation/readUsageCalculation"
import { parseTariffMonthlyRateResponse } from "@/models/tariffCompared/tariffCompared.parser"
import { TariffCompared } from "@/models/tariffCompared/tariffCompared"
import { ITariffCompared } from "@/models/tariffCompared/tariffCompared.types"
import {
  IMPANAddress,
  IMPRNAddress,
} from "@/models/meterpointAddress/meterpointAddress.types"
import {
  MPANAddressParser,
  MPRNAddressParser,
} from "@/models/meterpointAddress/meterpointAddress.parser"
import { METERPOINT_QUERY_STALE_TIME } from "@/constants/meterpoints"
import { HasuraQueries } from "@/api/hasura/queries"
import TrackingService from "@/services/TrackingService"
import { MeterPointAgreementParser } from "@/models/meterpointAgreement/meterpointAgreement.parser"
import { IMeterPointAgreement } from "@/models/meterpointAgreement/meterpointAgreement.types"
import {
  AgreementDetails,
  TariffAgreements as TariffAgreementsResponse,
  TariffMonthlyRateResponse,
  SearchMpanResponse,
  SearchMprnResponse,
} from "@/api/backofficeRest/generated/model"
import {
  GetAlpsRangeQuery,
  GetMeterpointSupplyHistoryQuery,
  MeterpointsByIdsQuery,
  GetRtsMetersQuery,
  GetSmartMetersEligibleForExchangeQuery,
} from "@/api/hasura/generated/graphql"

export interface IMeterpointService {
  getForAccount(accountNumber: string): QueryResult<IMeterpoint[]>
  getUnavailableForAccount(accountNumber: string): QueryResult<IMeterpoint[]>
  getMeterpointSupplyHistory(
    meterpointId: number,
  ): QueryResult<ISupplyHistory[]>
  getMeterpointTariffs(
    meterPointIdentifier: string,
    billingAccountId: number | null,
  ): QueryResult<ITariffAgreements>
  getMonthlyTariffRateComparisons(
    meterpointGsp: string,
    agreementRef: string,
  ): QueryResult<ITariffCompared>
  getLatestAccountRead(
    accountNumber: string,
  ): AggregatedQueryProps<IReading | null>
  getMPANsForPostcode(postcode: string): QueryResult<IMPANAddress[]>
  getMPRNsForPostcode(postcode: string): QueryResult<IMPRNAddress[]>
  getRtsMeters(serialNumbers: Array<string>): QueryResult<boolean>
  getMeterPointIdentifierMap(
    meterpointIds: number[],
  ): QueryResult<Record<number, string>>
  getSmartMetersEligibleForExchange(
    meterSerialNumbers: Array<string>,
  ): QueryResult<
    Array<{
      meterSerialNumber: string
      exchangeEligibilityReason: string | null
    }>
  >
}

// One fetch backs both getForAccount and getUnavailableForAccount, which only
// differ in their `select`.
const accountMeterpointsQuery = (accountNumber: string) => ({
  ...BackofficeRestQueries.meterpoints.forAccount(accountNumber),
  placeholderData: [] as IMeterpoint[],
  useRawResponse: true,
  parserFn: (meterpointsRaw: IMeterpointRaw[]) => {
    const meterpoints = meterpointsRaw ?? []

    if (!meterpoints.length) {
      return []
    }

    TrackingService.sendEvent("Meterpoints:load:success")
    return meterpoints
      .map(MeterpointParser.toDomain)
      .sort((a, b) => (a.meterpointType < b.meterpointType ? -1 : 1))
  },
  staleTime: METERPOINT_QUERY_STALE_TIME,
})

class MeterpointServiceClass implements IMeterpointService {
  // Meterpoints whose Junifer data loaded. Placeholders (dataAvailable false)
  // are left out so consumers relying on DNO/MOP or register usage are unaffected.
  getForAccount(accountNumber: string): QueryResult<IMeterpoint[]> {
    return useQuery({
      ...accountMeterpointsQuery(accountNumber),
      select: (meterpoints) =>
        meterpoints.filter((meterpoint) => meterpoint.dataAvailable),
    })
  }

  // Meterpoints the backend returned as placeholders because their Junifer data
  // failed to load.
  getUnavailableForAccount(accountNumber: string): QueryResult<IMeterpoint[]> {
    return useQuery({
      ...accountMeterpointsQuery(accountNumber),
      observerKey: "unavailable",
      select: (meterpoints) =>
        meterpoints.filter((meterpoint) => !meterpoint.dataAvailable),
    })
  }

  getMeterpointSupplyHistory(
    meterpointId: number,
  ): QueryResult<ISupplyHistory[]> {
    return useQuery({
      ...HasuraQueries.meterpoints.supplyHistory(meterpointId),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data: GetMeterpointSupplyHistoryQuery) => {
        return MeterpointSupplyHistoryParser.toDomain(data.meter_point_history)
      },
    })
  }

  getMeterpointTariffs(
    meterPointIdentifier: string,
    billingAccountId: number | null,
  ): QueryResult<ITariffAgreements> {
    return useQuery({
      ...BackofficeRestQueries.meterpointAgreements.getTariffAgreements(
        billingAccountId ?? 0,
        meterPointIdentifier,
      ),
      placeholderData: {} as ITariffAgreements,
      useRawResponse: true,
      parserFn: (rawData: TariffAgreementsResponse) =>
        rawData
          ? BackofficeTariffAgreementsParser.toDomain(rawData)
          : new TariffAgreements({ id: 0 }),
      enabled: !!meterPointIdentifier && !!billingAccountId,
    })
  }

  getMonthlyTariffRateComparisons(meterpointGsp: string, agreementRef: string) {
    return useQuery({
      ...BackofficeRestQueries.account.tariffMonthlyRates(
        agreementRef,
        meterpointGsp,
      ),
      useRawResponse: true,

      parserFn: (rawData: TariffMonthlyRateResponse) =>
        rawData
          ? parseTariffMonthlyRateResponse(rawData)
          : new TariffCompared(),
    })
  }

  getLatestAccountRead(accountNumber: string) {
    const queries = new QueryAggregator()
    const meterpointsQuery = this.getForAccount(accountNumber)
    queries.add(meterpointsQuery)

    const latestRead = meterpointsQuery.data.reduce(
      (latestRead: IReading | null, meterpoint: IMeterpoint) => {
        if (!latestRead) {
          return meterpoint.latestRead || latestRead
        }

        if (
          meterpoint.latestRead?.date &&
          (!latestRead.date ||
            latestRead.date.isBefore(meterpoint.latestRead.date))
        ) {
          return meterpoint.latestRead
        }

        return latestRead
      },
      null,
    )

    return queries.setData(latestRead).getAggregate()
  }

  getUsageFromReadingDifference(
    olderReading: IReading,
    newerReading: IReading,
  ) {
    const startDate = olderReading.date.format()
    const endDate = newerReading.date.format()
    const loadProfileQuery = useQuery({
      ...HasuraQueries.energyUsageLoadFactors.alpsRange(
        buildAlpsRangeVariables(startDate, endDate),
      ),
      useRawResponse: true,
      parserFn: (rawData: GetAlpsRangeQuery) =>
        AlpsRangeParser.toDomain(rawData, startDate, endDate),
    })

    return mapQueryResult(
      loadProfileQuery,
      (query) =>
        new ReadUsageCalculation(olderReading, newerReading, query.data),
    )
  }

  getMeterpointsIdentifiersByIds(meterpointIds: number[]) {
    return useQuery({
      ...HasuraQueries.meterpoints.meterpointsByIds({ meterpointIds }),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (rawData: MeterpointsByIdsQuery) => {
        return rawData.meter_point.map(({ identifier }) => identifier)
      },
    })
  }

  // Distinct key from getMeterpointsIdentifiersByIds: useQuery caches by
  // String(queryKey), so a shared key would collide two different parsed shapes.
  getMeterPointIdentifierMap(
    meterpointIds: number[],
  ): QueryResult<Record<number, string>> {
    return useQuery({
      ...HasuraQueries.meterpoints.meterpointsByIds({ meterpointIds }),
      queryKey: ["meterPointIdentifierMap", JSON.stringify(meterpointIds)],
      placeholderData: {},
      useRawResponse: true,
      parserFn: (rawData: MeterpointsByIdsQuery) =>
        rawData.meter_point.reduce<Record<number, string>>((map, mp) => {
          map[Number(mp.id)] = mp.identifier
          return map
        }, {}),
    })
  }

  getMPANsForPostcode(postcode: string): QueryResult<IMPANAddress[]> {
    return useQuery({
      ...BackofficeRestQueries.meterpoints.searchMpan(postcode),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data: SearchMpanResponse) =>
        (data.results ?? []).map((result) =>
          MPANAddressParser.toDomain(result),
        ),
      enabled: !!postcode,
      showSnackbarError: true,
      meta: {
        errorMessage: `Failed to search electricity meterpoints for postcode: ${postcode}`,
      },
    })
  }
  getMPRNsForPostcode(postcode: string): QueryResult<IMPRNAddress[]> {
    return useQuery({
      ...BackofficeRestQueries.meterpoints.searchMprn(postcode),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data: SearchMprnResponse) =>
        (data.results ?? []).map((result) =>
          MPRNAddressParser.toDomain(result),
        ),
      enabled: !!postcode,
      showSnackbarError: true,
      meta: {
        errorMessage: `Failed to search gas meterpoints for postcode: ${postcode}`,
      },
    })
  }

  getRtsMeters(msns: Array<string>): QueryResult<boolean> {
    return useQuery({
      ...HasuraQueries.meterpoints.rtsMeters({ serialNumbers: msns }),
      placeholderData: false,
      useRawResponse: true,
      parserFn: (data: GetRtsMetersQuery) => {
        const rtsCount = data?.rts_meters_report_aggregate?.aggregate?.count
        return Boolean(rtsCount && rtsCount > 0)
      },
    })
  }

  getSmartMetersEligibleForExchange(
    meterSerialNumbers: Array<string>,
  ): QueryResult<
    Array<{
      meterSerialNumber: string
      exchangeEligibilityReason: string | null
    }>
  > {
    return useQuery({
      ...HasuraQueries.meterpoints.smartMetersEligibleForExchange({
        meter_serial_numbers: meterSerialNumbers,
      }),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data: GetSmartMetersEligibleForExchangeQuery) =>
        (data?.smart_meters_eligible_for_exchange ?? []).map((row) => ({
          meterSerialNumber: row.meter_serial_number,
          exchangeEligibilityReason: row.exchange_eligibility_reason ?? null,
        })),
    })
  }

  getMeterPointAgreements(
    billingAccountId: number,
    meterPointIdentifier: string,
  ): QueryResult<IMeterPointAgreement[]> {
    return useQuery({
      ...BackofficeRestQueries.meterpointAgreements.getMeterPointAgreements(
        billingAccountId,
        meterPointIdentifier,
      ),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (rawData: AgreementDetails[]) =>
        (rawData ?? []).map((item) => MeterPointAgreementParser.toDomain(item)),
    })
  }
}

export const MeterpointService = new MeterpointServiceClass()
